import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase URL and Anon Key must be set in environment variables.');
}

const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Updates the last_activity timestamp for a studio
 * @param studioId - The ID of the studio to update
 * @returns Promise<boolean> - True if successful, false otherwise
 */
export const updateStudioLastActivity = async (studioId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('studios')
      .update({ 
        last_activity: new Date().toISOString() 
      })
      .eq('id', studioId);

    if (error) {
      console.error('Failed to update studio last_activity:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating studio last_activity:', error);
    return false;
  }
};

/**
 * Updates the last_activity timestamp for the current user's studio.
 * Works for both main accounts (Supabase JWT) and sub-accounts (backend session token).
 * @param accessToken - The auth token (from session.access_token or sub-account sessionToken). Pass from AuthContext.getAuthToken() or after login.
 * @returns Promise<boolean> - True if successful, false otherwise
 */
export const updateCurrentStudioLastActivity = async (accessToken: string | null): Promise<boolean> => {
  try {
    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    if (!apiUrl) {
      console.error('Backend URL not configured');
      return false;
    }

    if (!accessToken || accessToken.length < 10) {
      return false;
    }

    // Call the activity update endpoint (accepts both Supabase JWT and sub-account token via authenticateUser)
    const response = await fetch(`${apiUrl}/api/supabase/activity/update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to update activity:', response.statusText);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating current studio last_activity:', error);
    return false;
  }
};

/**
 * Gets the last activity timestamp for a studio
 * @param studioId - The ID of the studio
 * @returns Promise<string | null> - The last activity timestamp or null if not found
 */
export const getStudioLastActivity = async (studioId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('studios')
      .select('last_activity')
      .eq('id', studioId)
      .single();

    if (error) {
      console.error('Failed to get studio last_activity:', error);
      return null;
    }

    return data?.last_activity || null;
  } catch (error) {
    console.error('Error getting studio last_activity:', error);
    return null;
  }
};

/**
 * Formats the last activity timestamp for display
 * @param timestamp - The timestamp to format
 * @returns string - Formatted timestamp
 */
export const formatLastActivity = (timestamp: string | null): string => {
  if (!timestamp) return 'Never';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  
  return date.toLocaleDateString();
}; 